import './dynamicInputs.less'
import Label from '../Label'
import { isDuplicate } from '../../utils'
import { Button, Input, Typography } from '@lightbeamai/design-system'
import classNames from 'classnames'
import { MinusCircle, PlusCircle } from 'phosphor-react'
import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'

interface Props extends React.HTMLProps<HTMLDivElement> {
  inputs: string[]
  setInputs: (inputs: string[]) => void
  allowDuplicate?: boolean
  required?: boolean
  transId?: string
  placeholderId?: string
  suffixContent?: React.ReactNode[]
  warn?: (value: string) => { valid: boolean; rule?: string }
  validate?: (value: string) => { valid: boolean; rule?: string }
  messages?: Array<{ type: 'error' | 'warning'; value: React.ReactNode; rule?: string }>
}
export default function DynamicInputs({
  inputs,
  setInputs,
  className,
  allowDuplicate = false,
  required = true,
  transId = 'dynamicInputLabel',
  placeholderId,
  suffixContent,
  disabled,
  validate,
  warn,
  messages,
  ...rest
}: Props) {
  const intl = useIntl()
  const [touched, setTouched] = useState<boolean[]>([])
  const addInput = (index: number) => {
    const newInputs = [...inputs]
    newInputs.splice(index + 1, 0, '')
    setInputs(newInputs)
  }

  useEffect(() => {
    if (inputs.length < touched.length) {
      setTouched(inputs.map((_, idx) => touched[idx] ?? false))
    }
  }, [inputs])

  const removeInput = (index: number) => {
    if (inputs.length > 1) {
      const newInputs = inputs.filter((_, i) => i !== index)
      setInputs(newInputs)
    }
  }

  const handleInputChange = (index: number, value: string) => {
    const newInputs = [...inputs]
    newInputs[index] = value
    setInputs(newInputs)
  }

  const handleFocus = (index: number) => {
    const newInputs = [...inputs]
    const newTouched = [...touched]
    newTouched[index] = true
    setTouched(newTouched)
    newInputs[index] = newInputs[index].trim()
    setInputs(newInputs)
  }

  return (
    <div className={classNames('dynamic-inputs', className)} {...rest}>
      {inputs.map((input, index) => {
        const placeholder = intl.formatMessage({ id: transId }, { index: index + 1 })

        const validateResult = validate ? validate(input) : { valid: true }
        const warnResult = warn ? warn(input) : { valid: true }
        const s = warn ? (warnResult.valid ? 'warning' : '') : ''
        const e = validate ? (validateResult.valid ? '' : 'error') : ''
        const status = e || s

        const msg = messages?.find((msg) => msg.type === status && msg.rule === validateResult.rule)
        const showMsg = touched[index] && msg

        const isDuplicateVal = isDuplicate(inputs, input) && !allowDuplicate

        return (
          <div className="dynamic-input-wrapper" key={index}>
            <Label variant="base" text={placeholder} required={required} />
            <div key={index} className="dynamic-input">
              <Input
                type="text"
                value={input}
                onChange={(e) => handleInputChange(index, e.target.value)}
                placeholder={
                  placeholderId ? intl.formatMessage({ id: placeholderId }) : placeholder
                }
                onBlur={() => handleFocus(index)}
                status={touched[index] ? (isDuplicateVal ? 'error' : status) : ''}
                disabled={disabled}
                className="dynamic-input-field"
              />

              {!disabled && (
                <>
                  <div className="flex-row">
                    <Button
                      variant="link"
                      size={24}
                      onClick={() => removeInput(index)}
                      disabled={inputs.length === 1}
                      aria-label="Remove input"
                      icon={<MinusCircle size={18} weight="fill" color="var(--lb-color-red-500)" />}
                    />
                    <Button
                      variant="link"
                      size={24}
                      onClick={() => addInput(index)}
                      aria-label="Remove input"
                      icon={
                        <PlusCircle size={18} weight="fill" color="var(--lb-color-purple-500)" />
                      }
                    />
                  </div>
                  {suffixContent?.[index]}
                </>
              )}
            </div>
            {isDuplicateVal && (
              <Typography color="error" variant="baseMinus2" className="dynamic-input-message">
                {intl.formatMessage({
                  id: 'duplicateValue'
                })}
              </Typography>
            )}
            {showMsg && <div className="dynamic-input-message">{msg.value}</div>}
          </div>
        )
      })}
    </div>
  )
}
